import { ItemType } from '@openai/realtime-api-beta/dist/lib/client';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface Feedback {
  strengths: string[];
  areasForImprovement: string[];
  actionableNextSteps: {
    type: string;
    description: string;
  }[];
  scenarioOverview: string;
  objectiveAssessment: string;
}

type CoachState = {
  userId: string;
  userName: string;
  companyName: string;
  sessionEndsAt: number;
  browserSessionId?: string;
  deviceId?: string;
  function: string;
  category: string;
  lastSessionId: string | null;
  lastFeedback: Feedback | null;
  sessionTranscript: string | null;
  googleAuth?: {
    accessToken?: string;
    isAuthorized?: boolean;
  };
};

type CoachActions = {
  setUser: (user: {
    userId: string;
    deviceId?: string;
    name: string;
    function: string;
    category?: string;
    browserSessionId?: string;
    companyName?: string;
  }) => void;
  setGoogleAuth: (auth: {
    accessToken?: string;
    isAuthorized?: boolean;
  }) => void;
  clearUser: () => void;
  setLastSessionId: (sessionId: string) => void;
  setLastFeedback: (feedback: Feedback) => void;
  setSessionTranscript: (transcript: string) => void;
  setSessionEndsAt: (sessionEndsAt: number) => void;
};

export const useCoachStore = create<CoachState & CoachActions>()(
  devtools(
    persist(
      (set) => ({
        userId: '',
        userName: '',
        deviceId: '',
        function: '',
        category: '',
        lastSessionId: null,
        lastFeedback: null,
        sessionTranscript: null,
        googleAuth: undefined,
        companyName: '',
        sessionEndsAt: -1,
        setUser: (user) =>
          set({
            userId: user.userId,
            userName: user.name,
            deviceId: user.deviceId,
            function: user.function,
            category: user.category,
            companyName: user.companyName,
          }),
        setGoogleAuth: (auth) =>
          set((state) => ({
            googleAuth: {
              ...state.googleAuth,
              ...auth,
            },
          })),
        clearUser: () =>
          set({
            userId: '',
            userName: '',
            deviceId: '',
            function: '',
            category: '',
            companyName: '',
            lastFeedback: null,
            googleAuth: undefined,
          }),
        setLastSessionId: (sessionId) => set({ lastSessionId: sessionId }),
        setLastFeedback: (feedback) => set({ lastFeedback: feedback }),
        setSessionTranscript: (transcript) =>
          set({ sessionTranscript: transcript }),
        setSessionEndsAt: (sessionEndsAt) => set({ sessionEndsAt }),
      }),
      { name: 'coach-storage' },
    ),
  ),
);
